/**
 * GA Dashboard PDF export — page list, optional chrome, and jsPDF stamping.
 * Chrome pieces match the branded one-pager (logo, P6 meta, legend, portal).
 *
 * Balloon wording is painted with canvas 2D after html2canvas. html2canvas
 * stacks flex / unitless line-height / translate() chips on top of each other
 * (the 71940 Presentation PDF bug). The HTML chips stay on screen for drag;
 * the clone hides them and we draw each line at an explicit baseline.
 */

import { layoutBalloonChip } from './gaBalloonUtils';

export const PDF_EXPORT_STORAGE_KEY = 'ga-dashboard-pdf-export';

export const PDF_CHROME_ITEMS = [
  { key: 'companyHeader', label: 'Company header', hint: 'Logo, job name, dashboard title, company name' },
  { key: 'estimateP6', label: 'Estimate / P6 revision', hint: 'Estimate / P6 26LG0052-Current' },
  { key: 'dataDate', label: 'Data date', hint: 'Data date 2026-09-03' },
  { key: 'source', label: 'Schedule source', hint: 'Live P6' },
  { key: 'labor', label: 'Labor source', hint: 'Labor: resource (TASKRSRC)' },
  { key: 'pct', label: 'Percent method', hint: 'Pct: weighted Act÷Bud' },
  { key: 'legend', label: 'Status legend', hint: 'Not started · In progress · Complete · Late' },
  { key: 'printed', label: 'Printed timestamp', hint: 'Printed 2026-09-03, 07:45' },
  { key: 'portalUrl', label: 'Portal link', hint: 'https://portal.laframboise.net/project/…' },
  { key: 'portalHost', label: 'Portal host', hint: 'portal.laframboise.net' },
];

export const DEFAULT_PDF_CHROME_ITEMS = PDF_CHROME_ITEMS.reduce((acc, row) => {
  acc[row.key] = true;
  return acc;
}, {});

export const DEFAULT_PDF_EXPORT_OPTS = {
  includeChrome: true,
  pageScope: 'all',
  items: { ...DEFAULT_PDF_CHROME_ITEMS },
};

export function normalizePdfExportOpts(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const items = {};
  for (const { key } of PDF_CHROME_ITEMS) {
    items[key] = src.items && Object.prototype.hasOwnProperty.call(src.items, key)
      ? !!src.items[key]
      : DEFAULT_PDF_CHROME_ITEMS[key];
  }
  return {
    includeChrome: src.includeChrome !== false,
    pageScope: src.pageScope === 'current' ? 'current' : 'all',
    items,
  };
}

export function loadPdfExportOpts() {
  try {
    const raw = localStorage.getItem(PDF_EXPORT_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PDF_EXPORT_OPTS, items: { ...DEFAULT_PDF_CHROME_ITEMS } };
    return normalizePdfExportOpts(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_PDF_EXPORT_OPTS, items: { ...DEFAULT_PDF_CHROME_ITEMS } };
  }
}

export function savePdfExportOpts(opts) {
  const next = normalizePdfExportOpts(opts);
  try {
    localStorage.setItem(PDF_EXPORT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function pagesToExport({ pageScope, pageCount, activePage }) {
  const total = Math.max(1, parseInt(pageCount, 10) || 1);
  const current = Math.min(total, Math.max(1, parseInt(activePage, 10) || 1));
  if (pageScope === 'current' || total === 1) return [current];
  return Array.from({ length: total }, (_, i) => i + 1);
}

export function buildMetaBits(items, ctx = {}) {
  const bits = [];
  if (items?.estimateP6 && ctx.p6Short) bits.push(`Estimate / P6 ${ctx.p6Short}`);
  if (items?.dataDate && ctx.dataDate) bits.push(`Data date ${ctx.dataDate}`);
  if (items?.source) bits.push(ctx.source === 'p6' ? 'Live P6' : 'Imported snapshot');
  if (items?.labor) {
    bits.push(ctx.unitsMode === 'taskrsrc' ? 'Labor: resource (TASKRSRC)' : 'Labor: activity (TASK)');
  }
  if (items?.pct) {
    bits.push(ctx.pctMode === 'average' ? 'Pct: average' : 'Pct: weighted Act÷Bud');
  }
  return bits;
}

export function hexRgb(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return [100, 116, 139];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/**
 * Vertical room for optional chrome. Full branded pack keeps the original 96pt
 * image start so existing one-pagers do not shift.
 */
export function measureChrome(opts) {
  const include = !!opts?.includeChrome;
  const items = include
    ? { ...DEFAULT_PDF_CHROME_ITEMS, ...(opts.items || {}) }
    : {};
  const margin = include ? 28 : 12;

  if (!include) {
    return {
      include,
      items,
      margin,
      top: 12,
      footerH: 0,
      metaY: 0,
      legendY: 0,
      hasHeader: false,
      hasPrinted: false,
      hasMeta: false,
      hasLegend: false,
      hasFooter: false,
    };
  }

  const hasHeader = !!items.companyHeader;
  const hasPrinted = !!items.printed;
  const hasMeta = !!(items.estimateP6 || items.dataDate || items.source || items.labor || items.pct);
  const hasLegend = !!items.legend;
  const hasFooter = !!(items.portalUrl || items.portalHost);

  let y = 8;
  if (hasHeader) y = 56;
  else if (hasPrinted) y = 28;

  let metaY = 0;
  if (hasMeta) {
    metaY = hasHeader ? 66 : y + 12;
    y = metaY;
  }

  let legendY = 0;
  if (hasLegend) {
    legendY = hasMeta ? y + 14 : (hasHeader ? 80 : y + 14);
    y = legendY;
  }

  const fullBrand = hasHeader && hasMeta && hasLegend;
  const top = fullBrand ? 96 : (y === 8 ? 16 : y + 16);

  return {
    include,
    items,
    margin,
    top,
    footerH: hasFooter ? 22 : 8,
    metaY,
    legendY,
    hasHeader,
    hasPrinted,
    hasMeta,
    hasLegend,
    hasFooter,
  };
}

export function waitFor(predicate, { timeout = 25000, interval = 50 } = {}) {
  const started = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      try {
        if (predicate()) {
          resolve(true);
          return;
        }
      } catch {
        /* keep waiting */
      }
      if (Date.now() - started >= timeout) {
        reject(new Error('Timed out waiting for the drawing to finish rendering'));
        return;
      }
      setTimeout(tick, interval);
    };
    tick();
  });
}

export async function loadImageDataUrl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = src;
  });
}

export function readBalloonChipsFromStage(stage) {
  if (!stage || typeof stage.querySelectorAll !== 'function') return [];
  return [...stage.querySelectorAll('[data-ga-balloon]')].map((el) => {
    let model = {};
    try {
      model = JSON.parse(el.getAttribute('data-ga-balloon') || '{}');
    } catch {
      model = {};
    }
    const cx = Number(el.getAttribute('data-ga-cx'));
    const cy = Number(el.getAttribute('data-ga-cy'));
    return {
      ...model,
      cx: Number.isFinite(cx) ? cx : (parseFloat(el.style.left) || 0),
      cy: Number.isFinite(cy) ? cy : (parseFloat(el.style.top) || 0),
    };
  }).filter((chip) => Array.isArray(chip.lines) && chip.lines.length);
}

/** Hide HTML chips and drop ancestor CSS transforms that scramble html2canvas. */
export function prepareGaStageClone(clonedStage) {
  if (!clonedStage) return clonedStage;
  clonedStage.querySelectorAll('[data-ga-balloon]').forEach((el) => {
    el.style.visibility = 'hidden';
    el.style.display = 'none';
  });
  let node = clonedStage.parentElement;
  const root = clonedStage.ownerDocument?.documentElement;
  while (node && node !== root) {
    if (node.style) node.style.transform = 'none';
    node = node.parentElement;
  }
  return clonedStage;
}

function canvasFont(weight, size, italic) {
  return `${italic ? 'italic ' : ''}${weight || 400} ${Math.max(1, size)}px "Segoe UI", Arial, sans-serif`;
}

function roundRectPath(ctx, x, y, w, h, r) {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

export function paintBalloonChip(ctx, chip, { cx, cy, pixelRatio = 1 } = {}) {
  if (!ctx || !chip) return null;
  const scaled = { ...chip, scale: (Number(chip.scale) > 0 ? Number(chip.scale) : 1) * pixelRatio };
  const measure = (text, fontSize, fontWeight) => {
    ctx.font = canvasFont(fontWeight, fontSize, false);
    return ctx.measureText(String(text || '')).width;
  };
  const layout = layoutBalloonChip(scaled, measure);
  const left = cx - layout.width / 2;
  const top = cy - layout.height;
  const s = layout.scale;
  const border = Math.max(1.5 * pixelRatio, 2 * s);

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.28)';
  ctx.shadowBlur = 6 * pixelRatio;
  ctx.shadowOffsetY = 2 * pixelRatio;
  ctx.fillStyle = chip.fill || 'rgba(255,255,255,0.97)';
  roundRectPath(ctx, left, top, layout.width, layout.height, 7 * s);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = chip.borderColor || chip.color || '#64748b';
  ctx.lineWidth = border;
  ctx.stroke();

  for (const line of layout.lines) {
    if (line.kind === 'title') {
      const dotR = layout.dot / 2;
      ctx.beginPath();
      ctx.fillStyle = chip.color || '#64748b';
      ctx.arc(left + layout.padX + dotR, top + line.y - line.fontSize * 0.35, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.font = canvasFont(line.fontWeight, line.fontSize, line.italic);
    ctx.fillStyle = line.color || '#0f172a';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(line.text, left + line.x, top + line.y);
  }
  ctx.restore();
  return { left, top, width: layout.width, height: layout.height, lines: layout.lines };
}

export function paintBalloonChipsOnCanvas(canvas, chips, { stageWidth, stageHeight } = {}) {
  if (!canvas || !chips?.length) return canvas;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const pixelRatio = canvas.width / Math.max(1, stageWidth || canvas.width);
  chips.forEach((chip) => {
    paintBalloonChip(ctx, chip, {
      cx: chip.cx * pixelRatio,
      cy: chip.cy * pixelRatio,
      pixelRatio,
    });
  });
  return canvas;
}

/**
 * Chromium will not composite further 2d draws onto the canvas html2canvas
 * returns (fillText / fillRect are silent no-ops). Copy to a fresh canvas
 * before painting balloons — verified on portal.laframboise.net 2026-09-05.
 */
export function drawableCanvasCopy(source) {
  if (!source || typeof document === 'undefined') return source;
  try {
    const copy = document.createElement('canvas');
    copy.width = source.width || 1;
    copy.height = source.height || 1;
    const ctx = copy.getContext('2d');
    if (!ctx || typeof ctx.drawImage !== 'function') return source;
    ctx.drawImage(source, 0, 0);
    return copy;
  } catch {
    return source;
  }
}

/**
 * Photograph the drawing + SVG overlay, then stamp balloons with canvas text
 * so wording can never collapse onto one baseline.
 */
export async function captureGaStage(stage, html2canvas, { scale = 2 } = {}) {
  if (!stage) throw new Error('Drawing is not ready');
  const chips = readBalloonChipsFromStage(stage);
  const stageWidth = stage.offsetWidth || stage.getBoundingClientRect?.().width || 1;
  const snapshot = await html2canvas(stage, {
    scale,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    ignoreElements: (el) => !!(el && typeof el.hasAttribute === 'function' && el.hasAttribute('data-ga-balloon')),
    onclone: (_doc, cloned) => {
      if (cloned) prepareGaStageClone(cloned);
    },
  });
  const canvas = drawableCanvasCopy(snapshot);
  paintBalloonChipsOnCanvas(canvas, chips, { stageWidth, stageHeight: stage.offsetHeight });
  return canvas;
}

export function makePlaceholderCanvas(page, message) {
  const canvas = document.createElement('canvas');
  canvas.width = 1600;
  canvas.height = 900;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#334155';
  ctx.font = '600 28px sans-serif';
  ctx.fillText(`Page ${page} could not be captured`, 64, 200);
  ctx.fillStyle = '#64748b';
  ctx.font = '16px sans-serif';
  ctx.fillText(String(message || 'The slide did not finish rendering in time.'), 64, 240);
  return canvas;
}

const LEGEND = [
  { key: 'not_started', t: 'Not started' },
  { key: 'in_progress', t: 'In progress' },
  { key: 'complete', t: 'Complete' },
  { key: 'late', t: 'Late' },
];

/**
 * Stamp one landscape letter page: optional chrome + the captured slide.
 */
export function stampGaPdfPage(pdf, {
  canvas,
  layout,
  ctx,
  addPage,
}) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  if (addPage) pdf.addPage();

  const { margin, items } = layout;

  if (layout.hasHeader) {
    pdf.setFillColor(185, 28, 28);
    pdf.rect(0, 0, pageW, 4, 'F');

    let titleX = margin;
    let drewLogo = false;
    if (ctx.logoDataUrl) {
      try {
        pdf.addImage(ctx.logoDataUrl, 'PNG', margin, 12, 36, 36);
        titleX = margin + 44;
        drewLogo = true;
      } catch {
        drewLogo = false;
      }
    }
    if (!drewLogo) {
      pdf.setFontSize(16);
      pdf.setTextColor(185, 28, 28);
      pdf.setFont(undefined, 'bold');
      pdf.text('LGL', margin, 28);
      titleX = margin + 36;
    }

    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(15, 23, 42);
    pdf.text(ctx.projectLine || '', titleX, 22);
    pdf.setFontSize(11);
    pdf.text(`GA Dashboard — ${ctx.dashboardName || 'export'}`, titleX, 38);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text(ctx.legalName || 'Laframboise Group Ltd.', titleX, 50);
  }

  if (layout.hasPrinted && ctx.printedAt) {
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text(`Printed ${ctx.printedAt}`, pageW - margin, layout.hasHeader ? 22 : 18, { align: 'right' });
  }

  if (layout.hasMeta && ctx.metaBits?.length) {
    pdf.setFontSize(8.5);
    pdf.setTextColor(71, 85, 105);
    pdf.setFont(undefined, 'normal');
    pdf.text(ctx.metaBits.join('  ·  '), margin, layout.metaY);
  }

  if (layout.hasLegend) {
    const colors = ctx.statusColors || {};
    let lx = margin;
    LEGEND.forEach(({ key, t }) => {
      const [r, g, b] = hexRgb(colors[key]);
      pdf.setFillColor(r, g, b);
      pdf.circle(lx + 4, layout.legendY - 2, 4, 'F');
      pdf.setTextColor(51, 65, 85);
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');
      pdf.text(t, lx + 12, layout.legendY);
      lx += pdf.getTextWidth(t) + 28;
    });
  }

  if (canvas) {
    const img = typeof canvas.toDataURL === 'function'
      ? canvas.toDataURL('image/jpeg', 0.92)
      : canvas;
    const srcW = canvas.width || 1600;
    const srcH = canvas.height || 900;
    const maxW = pageW - margin * 2;
    const maxH = pageH - layout.top - margin - layout.footerH;
    const ratio = Math.min(maxW / srcW, maxH / srcH);
    const w = srcW * ratio;
    const h = srcH * ratio;
    pdf.addImage(img, 'JPEG', margin, layout.top, w, h);
  }

  if (layout.hasFooter) {
    pdf.setDrawColor(226, 232, 240);
    pdf.line(margin, pageH - layout.footerH - 4, pageW - margin, pageH - layout.footerH - 4);
    pdf.setFontSize(8);
    if (items.portalUrl && ctx.portalUrl) {
      pdf.setTextColor(15, 118, 110);
      if (typeof pdf.textWithLink === 'function') {
        pdf.textWithLink(ctx.portalUrl, margin, pageH - 10, { url: ctx.portalUrl });
      } else {
        pdf.text(ctx.portalUrl, margin, pageH - 10);
      }
    }
    if (items.portalHost) {
      pdf.setTextColor(100, 116, 139);
      pdf.text(ctx.portalHost || 'portal.laframboise.net', pageW - margin, pageH - 10, { align: 'right' });
    }
  }
}
